import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

import { Switch } from '../components/ui/switch';
import { Plus, Edit2, Trash2, Save, Search, RefreshCw, Download, Upload, Eye, Copy, Archive, BarChart3, FolderOpen, FileJson, FileText, Database, Star, StarOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollectionsManagementPro = () => {
  const { getAuthHeader } = useAuth();
  
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  const [currentCollection, setCurrentCollection] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    version: '1.0',
    is_active: true,
    is_default: false,
    metadata: {}
  });
  
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    totalNomenclatures: 0,
    totalCategories: 0
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCollections();
  }, [showArchived]);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/nomenclature-collections${showArchived ? '?include_archived=true' : ''}`,
        { headers: getAuthHeader() }
      );
      setCollections(response.data);
      calculateStats(response.data);
    } catch {
      toast.error('Erreur lors du chargement des collections');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      active: data.filter(c => c.is_active && !c.is_archived).length,
      archived: data.filter(c => c.is_archived).length,
      totalNomenclatures: data.reduce((sum, c) => sum + (c.nomenclatures_count || 0), 0),
      totalCategories: data.reduce((sum, c) => sum + (c.categories_count || 0), 0)
    });
  };

  const filteredCollections = collections.filter(c => {
    const matchSearch = !searchTerm || 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/nomenclature-collections`, formData, { headers: getAuthHeader() });
      toast.success('Collection créée avec succès');
      setCreateDialogOpen(false);
      resetForm();
      fetchCollections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    if (!currentCollection) return;
    try {
      await axios.put(
        `${API}/nomenclature-collections/${currentCollection.id}`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success('Collection mise à jour');
      setEditDialogOpen(false);
      resetForm();
      fetchCollections();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette collection et toutes ses données ?')) return;
    try {
      await axios.delete(`${API}/nomenclature-collections/${id}`, { headers: getAuthHeader() });
      toast.success('Collection supprimée');
      fetchCollections();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.patch(
        `${API}/nomenclature-collections/${id}/set-default`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Collection définie par défaut');
      fetchCollections();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.patch(
        `${API}/nomenclature-collections/${id}/archive`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Collection archivée');
      fetchCollections();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleDuplicate = async (collection) => {
    try {
      await axios.post(
        `${API}/nomenclature-collections`,
        {
          ...collection,
          id: undefined,
          code: `${collection.code}_COPY`,
          name: `${collection.name} (copie)`,
          is_active: false,
          is_default: false
        },
        { headers: getAuthHeader() }
      );
      toast.success('Collection dupliquée');
      fetchCollections();
    } catch {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleExport = (format) => {
    let content, filename, type;
    
    if (format === 'json') {
      content = JSON.stringify({ collections: filteredCollections, exported_at: new Date().toISOString() }, null, 2);
      filename = `collections_${new Date().toISOString().split('T')[0]}.json`;
      type = 'application/json';
    } else {
      const headers = ['Code', 'Nom', 'Version', 'Description', 'Actif', 'Défaut', 'Nomenclatures', 'Catégories'];
      const rows = filteredCollections.map(c => [
        c.code, c.name, c.version, c.description || '', 
        c.is_active ? 'Oui' : 'Non', c.is_default ? 'Oui' : 'Non',
        c.nomenclatures_count || 0, c.categories_count || 0
      ]);
      content = [headers.join(';'), ...rows.map(r => r.map(v => `"${v}"`).join(';'))].join('\n');
      filename = `collections_${new Date().toISOString().split('T')[0]}.csv`;
      type = 'text/csv;charset=utf-8;';
    }
    
    const blob = new Blob(['\uFEFF' + content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Export ${format.toUpperCase()} réussi !`);
    setExportDialogOpen(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      version: '1.0',
      is_active: true,
      is_default: false,
      metadata: {}
    });
    setCurrentCollection(null);
  };

  const openEditDialog = (collection) => {
    setCurrentCollection(collection);
    setFormData({
      name: collection.name || '',
      code: collection.code || '',
      description: collection.description || '',
      version: collection.version || '1.0',
      is_active: collection.is_active !== false,
      is_default: collection.is_default || false,
      metadata: collection.metadata || {}
    });
    setEditDialogOpen(true);
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/30 min-h-screen">
      
      {/* Bouton Retour */}
      <BackButton label="Retour" />

{/* Header Professionnel */}
      <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Database className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Gestion des Collections</h1>
              <p className="text-white/80 text-sm">Gérez vos ensembles de nomenclatures</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex gap-2">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-xs text-white/70">Collections</div>
              </div>
              <div className="bg-green-500/30 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.active}</div>
                <div className="text-xs text-white/70">Actives</div>
              </div>
              <div className="bg-purple-500/30 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-2xl font-bold">{stats.totalNomenclatures}</div>
                <div className="text-xs text-white/70">Nomenclatures</div>
              </div>
            </div>
            
            <Button onClick={() => setStatsDialogOpen(true)} variant="ghost" className="text-white hover:bg-white/20">
              <BarChart3 className="h-5 w-5" />
            </Button>
            <Button onClick={fetchCollections} variant="ghost" className="text-white hover:bg-white/20">
              <RefreshCw className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Barre d'outils */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher une collection..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-[300px]"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch checked={showArchived} onCheckedChange={setShowArchived} />
                <Label className="text-sm text-slate-600">Archivées</Label>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-1" /> Importer
              </Button>
              <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)}>
                <Download className="h-4 w-4 mr-1" /> Exporter
              </Button>
              <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="h-4 w-4 mr-1" /> Nouvelle collection
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grille des collections */}
      {loading ? (
        <Card className="p-12 text-center">
          <RefreshCw className="h-8 w-8 mx-auto animate-spin text-cyan-600 mb-4" />
          <p className="text-slate-600">Chargement...</p>
        </Card>
      ) : filteredCollections.length === 0 ? (
        <Card className="p-12 text-center">
          <Database className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucune collection</h3>
          <p className="text-slate-500 mb-4">Créez votre première collection de nomenclatures</p>
          <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Créer une collection
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCollections.map((collection) => (
            <Card 
              key={collection.id} 
              className={`border-0 shadow-lg hover:shadow-xl transition-all overflow-hidden ${collection.is_archived ? 'opacity-60' : ''}`}
            >
              <div className={`h-2 ${collection.is_default ? 'bg-gradient-to-r from-amber-400 to-yellow-500' : 'bg-gradient-to-r from-cyan-500 to-blue-500'}`} />
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${collection.is_default ? 'bg-amber-100' : 'bg-cyan-100'}`}>
                      <FolderOpen className={`h-6 w-6 ${collection.is_default ? 'text-amber-600' : 'text-cyan-600'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg text-slate-800">{collection.name}</h3>
                        {collection.is_default && (
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="font-mono text-xs bg-slate-700 text-white border-slate-800 font-semibold">{collection.code}</Badge>
                        <Badge variant="outline" className="text-xs">v{collection.version}</Badge>
                      </div>
                    </div>
                  </div>
                  <Badge className={collection.is_active ? 'bg-green-700 text-white' : 'bg-slate-700 text-white'}>
                    {collection.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
                
                {collection.description && (
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4">{collection.description}</p>
                )}
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-slate-700">{collection.nomenclatures_count || 0}</div>
                    <div className="text-xs text-slate-500">Nomenclatures</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <div className="text-xl font-bold text-slate-700">{collection.categories_count || 0}</div>
                    <div className="text-xs text-slate-500">Catégories</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 pt-3 border-t">
                  <Button variant="ghost" size="sm" onClick={() => { setCurrentCollection(collection); setViewDialogOpen(true); }}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(collection)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleSetDefault(collection.id)} title="Définir par défaut">
                    {collection.is_default ? <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> : <StarOff className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDuplicate(collection)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleArchive(collection.id)}>
                    <Archive className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(collection.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Création/Édition */}
      <Dialog open={createDialogOpen || editDialogOpen} onOpenChange={(open) => {
        if (!open) { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editDialogOpen ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {editDialogOpen ? 'Modifier la collection' : 'Nouvelle collection'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input 
                  value={formData.code} 
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="EX: BATICO-2024"
                />
              </div>
              <div className="space-y-2">
                <Label>Version</Label>
                <Input 
                  value={formData.version} 
                  onChange={(e) => setFormData({...formData, version: e.target.value})}
                  placeholder="1.0"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nom de la collection"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Description de la collection..."
                rows={3}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>Collection active</Label>
                <p className="text-xs text-slate-500">Disponible pour utilisation</p>
              </div>
              <Switch 
                checked={formData.is_active}
                onCheckedChange={(val) => setFormData({...formData, is_active: val})}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <Label className="text-amber-800">Collection par défaut</Label>
                <p className="text-xs text-amber-600">Utilisée automatiquement</p>
              </div>
              <Switch 
                checked={formData.is_default}
                onCheckedChange={(val) => setFormData({...formData, is_default: val})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>
              Annuler
            </Button>
            <Button onClick={editDialogOpen ? handleUpdate : handleCreate} className="bg-cyan-600 hover:bg-cyan-700">
              <Save className="h-4 w-4 mr-2" />
              {editDialogOpen ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Vue */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Détails de la collection</DialogTitle>
          </DialogHeader>
          {currentCollection && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className={`p-4 rounded-xl ${currentCollection.is_default ? 'bg-amber-100' : 'bg-cyan-100'}`}>
                  <FolderOpen className={`h-8 w-8 ${currentCollection.is_default ? 'text-amber-600' : 'text-cyan-600'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">{currentCollection.name}</h2>
                    {currentCollection.is_default && <Star className="h-5 w-5 text-amber-500 fill-amber-500" />}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="font-mono bg-slate-700 text-white border-slate-800 font-semibold">{currentCollection.code}</Badge>
                    <Badge variant="outline">v{currentCollection.version}</Badge>
                  </div>
                </div>
              </div>
              {currentCollection.description && (
                <p className="text-slate-600">{currentCollection.description}</p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 bg-gradient-to-br from-cyan-50 to-cyan-100">
                  <div className="text-2xl font-bold text-cyan-700">{currentCollection.nomenclatures_count || 0}</div>
                  <div className="text-sm text-cyan-600">Nomenclatures</div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
                  <div className="text-2xl font-bold text-blue-700">{currentCollection.categories_count || 0}</div>
                  <div className="text-sm text-blue-600">Catégories</div>
                </Card>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Fermer</Button>
            <Button onClick={() => { setViewDialogOpen(false); openEditDialog(currentCollection); }}>
              <Edit2 className="h-4 w-4 mr-2" /> Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Export */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" /> Exporter les collections
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-6">
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => handleExport('json')}>
              <FileJson className="h-8 w-8 text-amber-600" />
              <span>JSON</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => handleExport('csv')}>
              <FileText className="h-8 w-8 text-green-600" />
              <span>CSV</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Stats */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" /> Statistiques globales
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <Card className="p-4 bg-gradient-to-br from-cyan-50 to-cyan-100">
              <div className="text-3xl font-bold text-cyan-700">{stats.total}</div>
              <div className="text-sm text-cyan-600">Total collections</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
              <div className="text-3xl font-bold text-green-700">{stats.active}</div>
              <div className="text-sm text-green-600">Actives</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="text-3xl font-bold text-purple-700">{stats.totalNomenclatures}</div>
              <div className="text-sm text-purple-600">Nomenclatures totales</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="text-3xl font-bold text-blue-700">{stats.totalCategories}</div>
              <div className="text-sm text-blue-600">Catégories totales</div>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      <input ref={fileInputRef} type="file" accept=".json,.csv" className="hidden" />
    </div>
  );
};

export default CollectionsManagementPro;
